import React from 'react';
import Link from 'next/link';
import round from 'lodash/round';
import Button from '@mui/material/Button';
import HideChart from '@/components/HideChart';
import GeneralChart from '../GeneralChart';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import { CityContext } from '@/context/create-context';
import { TAB_SALARY_RANK_RATIO } from '@/constants/city';
import { useClintMediaQuery } from '@/utils/hooks/useClintMediaQuery';
import { trackClickEvent } from '@/utils/site-track';

const DEFAULT_COUNT = 5;

const Chart = ({
  data,
  cityPinyin,
}: {
  data: { avg: number; name: string; id: string }[];
  cityPinyin: string;
}) => {
  const chartRef = React.useRef(null);
  const isMobile = useClintMediaQuery();

  const config = React.useMemo<ECOption>(() => {
    const chartData = data
      .slice(0, DEFAULT_COUNT)
      .concat(
        new Array(DEFAULT_COUNT)
          .fill({ name: '', avg: undefined })
          .slice(data.length),
      );
    const chartName = chartData.map((item) => item.name);
    const chartSalary = chartData.map((item) => round(item.avg, -2));

    return {
      grid: {
        show: false,
        tooltip: { show: false },
        left: 24,
        right: 24,
        top: 20,
        bottom: 30,
      },
      xAxis: {
        type: 'category',
        data: chartName,
        show: false,
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        show: false,
      },
      series: [
        {
          type: 'bar',
          cursor: 'default',
          barWidth: isMobile ? 20 : 40,
          data: chartSalary,
          itemStyle: {
            borderRadius: isMobile ? 4 : 8,
            color: ({ dataIndex }) =>
              dataIndex === 0 ? 'rgb(76,163,143)' : 'rgba(123,192,174)',
          },
          label: {
            show: true,
            position: 'top',
            fontSize: 14,
            color: '#2d2d2d',
            formatter: ({ value }) => '¥' + value,
          },
        },
      ],
    };
  }, [isMobile, data]);

  return (
    <div className="tablet:flex">
      <div className="px-4.5 py-3 overflow-auto tablet:w-2/3">
        <div className="relative w-full h-60 min-h-full min-w-[410px]">
          <CusEcharts chartRef={chartRef} config={config} />
          <div className="absolute bottom-0 w-full h-[22px] min-w-[370px] px-3.5 text-xs !leading-5.5 flex justify-between items-center tablet:px-1">
            {data.slice(0, DEFAULT_COUNT).map(({ id, name }, index, array) => (
              <Link
                className="inline-block w-5 h-full relative tablet:w-10"
                key={id + index}
                href={`/${cityPinyin}/${id}/`}
                title={name}
                data-ele-group={`${TAB_SALARY_RANK_RATIO}_job-title`}
                data-ele-id={`job-title_${name}`}
                onClick={trackClickEvent}
              >
                <span className="underline absolute top-0 left-1/2 whitespace-nowrap -translate-x-1/2">
                  {(index > 0 &&
                    index < DEFAULT_COUNT - 1 &&
                    array[index + 1]?.name.length + name.length > 11) ||
                  ((index === 0 || index === DEFAULT_COUNT - 1) &&
                    (name.length > 7 ||
                      Number(name.match(/[\u4E00-\u9FA5]/g)?.length) > 6))
                    ? name.substring(0, DEFAULT_COUNT) + '...'
                    : name}
                </span>
              </Link>
            ))}
            {new Array(
              data.length < DEFAULT_COUNT ? DEFAULT_COUNT - data.length : 0,
            )
              .fill('')
              .map((item, index) => (
                <div className="h-full w-5 tablet:w-10" key={item + index} />
              ))}
          </div>
        </div>
      </div>
      <div className="pt-6 flex flex-col gap-y-2.5 tablet:w-1/3 tablet:pt-3 tablet:ml-6">
        {data.slice(DEFAULT_COUNT).map(({ id, name, avg }, index) => (
          <p key={index} className="bg-white pl-5 pr-3 py-3 flex items-center">
            <span className="text-xs !leading-5 w-8 text-center shrink-0">
              <span className="px-2 rounded-lg bg-web-green-400">
                {index + DEFAULT_COUNT + 1}
              </span>
            </span>
            <Link
              className="text-xs ml-5 underline line-clamp-1"
              href={`/${cityPinyin}/${id}/`}
              title={name}
            >
              {name}
            </Link>
            <span className="ml-auto text-sm">{`¥${round(avg, -2)}`}</span>
          </p>
        ))}
      </div>
    </div>
  );
};

const SalaryRankRatio = () => {
  const { pinyin, salary_top_ten: salaryTopTen } =
    React.useContext(CityContext);
  const [industryId, setIndustryId] = React.useState<string>(
    salaryTopTen[0].id,
  );

  return (
    <GeneralChart
      title="高薪职位月薪 Top 10"
      subTip="聚焦含金量最高的具体岗位，明确顶尖技术人才的薪酬标杆。"
    >
      <h3 className="flex mb-4 whitespace-nowrap overflow-auto">
        {salaryTopTen.map(({ id, name }, index) => (
          <Button
            key={index}
            onClick={(e) => {
              setIndustryId(id);
              id !== industryId && trackClickEvent(e);
            }}
            data-selected={id === industryId}
            className="!px-6 !py-2 !text-primary !bg-white/70 !font-semibold !text-sm !rounded-lg hover:!bg-black/4 data-[selected=true]:hover:!bg-blue-50 data-[selected=true]:!bg-web-blue-500/4 data-[selected=true]:!text-web-blue-500"
            data-ele-group={`${TAB_SALARY_RANK_RATIO}_industry`}
            data-ele-id={`industry_${name}`}
          >
            {name}
          </Button>
        ))}
      </h3>
      <div className="w-full">
        {salaryTopTen
          .filter(({ id }) => id === industryId)
          .map(({ id, job_titles }) => (
            <HideChart
              key={id}
              isHide={job_titles.length <= 0}
              hideTitle="该行业"
            >
              <Chart data={job_titles} cityPinyin={pinyin} />
            </HideChart>
          ))}
      </div>
    </GeneralChart>
  );
};

export default SalaryRankRatio;
